import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, ApiError } from '@/api/client';
import type { Page } from '@/api/types';
import { Icon } from '@/components/Icon';
import { importInitialStock, type InitialStockImportResult } from './api';
import s from './InitialStockImportDialog.module.css';

type Repair = { originalRow: number; message: string; warehouseCode: string; warehouseName: string; productCode: string; quantity: string; sourceWarehouseCode: string; sourceWarehouseName: string; sourceProductCode: string; sourceQuantity: string; unitCost: string; note: string; skipped: boolean };
type ProductSuggestion = { code: string; name: string };

function readField(data: Record<string, string>, names: string[]) {
  const normalized = Object.fromEntries(Object.entries(data).map(([key, value]) => [key.toLowerCase().replace(/\s+/g, ''), String(value || '')]));
  return names.map((name) => normalized[name.toLowerCase().replace(/\s+/g, '')]).find(Boolean) || '';
}

function repairsFrom(result: InitialStockImportResult, sourceRows?: Repair[]) {
  return result.errors.map((item) => {
    const source = sourceRows?.[item.rowNumber - 2];
    const data = item.rowData || {};
    return {
      originalRow: source?.originalRow || item.rowNumber,
      message: item.message,
      warehouseCode: readField(data, ['仓库编码', 'warehousecode']),
      warehouseName: readField(data, ['仓库名称', 'warehousename']),
      productCode: readField(data, ['产品编号', '产品编码', 'productcode', 'code']),
      quantity: readField(data, ['期初库存', '数量', '库存数量', 'quantity']),
      sourceWarehouseCode: readField(data, ['仓库编码', 'warehousecode']),
      sourceWarehouseName: readField(data, ['仓库名称', 'warehousename']),
      sourceProductCode: readField(data, ['产品编号', '产品编码', 'productcode', 'code']),
      sourceQuantity: readField(data, ['期初库存', '数量', '库存数量', 'quantity']),
      unitCost: readField(data, ['成本单价', '单价成本', 'unitcost']),
      note: readField(data, ['备注', 'note']),
      skipped: false,
    };
  });
}

function csvCell(value: string) { return `"${value.replace(/"/g, '""')}"`; }
function csvFor(rows: Repair[]) {
  return '\uFEFF' + [
    ['仓库编码', '仓库名称', '产品编号', '期初库存', '成本单价', '备注'].map(csvCell).join(','),
    ...rows.map((row) => [row.warehouseCode, row.warehouseName, row.productCode, row.quantity, row.unitCost, row.note].map(csvCell).join(',')),
  ].join('\r\n');
}

function downloadFailed(rows: Repair[]) {
  const blob = new Blob([csvFor(rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = '期初库存-待修正.csv';
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function repairTitle(row: Repair) {
  if (row.message.startsWith('产品编号不存在')) return '产品编号不存在';
  if (row.message.startsWith('仓库不存在')) return '仓库没匹配上';
  if (row.message.includes('期初库存不是数字')) return '期初库存不是数字';
  return row.message;
}

function repairSummary(row: Repair, productName?: string) {
  const productCode = row.sourceProductCode || row.productCode;
  const product = productName || productCode;
  if (row.message.startsWith('仓库不存在')) return `「${row.sourceWarehouseName || row.sourceWarehouseCode}」不是已有仓库 · ${product} · 期初 ${row.sourceQuantity}`;
  if (row.message.includes('期初库存不是数字')) return `「${row.sourceQuantity}」里有字母 O · ${product} ${productCode}`;
  return `${productCode} · ${row.sourceWarehouseName || row.sourceWarehouseCode} · 期初 ${row.sourceQuantity}`;
}

export function InitialStockImportDialog({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [mode, setMode] = useState<'set' | 'add'>('set');
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ filename: string; total: number; imported: number; jobId: string; repairs: Repair[] } | null>(null);
  const [expandedRepair, setExpandedRepair] = useState<number | null>(null);
  const [productHints, setProductHints] = useState<Record<number, { name: string; suggestion?: ProductSuggestion }>>({});

  useEffect(() => {
    if (!result) {
      setExpandedRepair(null);
      setProductHints({});
      return;
    }
    setExpandedRepair(result.repairs.findIndex((row) => !row.skipped));
    let cancelled = false;
    void Promise.all(result.repairs.map(async (row, index) => {
      if (!row.productCode) return null;
      try {
        const exact = await api.get<Page<ProductSuggestion>>('/api/products', { scope: 'inventory-options', keyword: row.productCode, pageSize: 20 });
        const product = exact.items.find((item) => item.code.toLowerCase() === row.productCode.toLowerCase());
        if (product) return [index, { name: product.name }] as const;
        if (!row.message.startsWith('产品编号不存在')) return null;
        const prefix = row.productCode.replace(/\d+$/, '');
        if (prefix === row.productCode) return null;
        const nearby = await api.get<Page<ProductSuggestion>>('/api/products', { scope: 'inventory-options', keyword: prefix, pageSize: 20 });
        const candidates = nearby.items.filter((item) => item.code.toLowerCase().startsWith(prefix.toLowerCase()));
        return candidates.length === 1 ? [index, { name: candidates[0].name, suggestion: candidates[0] }] as const : null;
      } catch {
        return null;
      }
    })).then((hints) => {
      if (!cancelled) setProductHints(Object.fromEntries(hints.filter((item): item is NonNullable<typeof item> => !!item)));
    });
    return () => { cancelled = true; };
  }, [result?.jobId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !loading) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, loading]);

  function selectFile(next: File | null) {
    setError('');
    setResult(null);
    setUncertain(false);
    if (!next) { setFile(null); return; }
    if (!/\.(xlsx|csv)$/i.test(next.name)) { setError('仅支持 .xlsx 或 .csv 文件'); return; }
    if (next.size > 12 * 1024 * 1024) { setError('单个文件不能超过 12MB'); return; }
    setFile(next);
  }

  async function upload(nextFile: File, retryRows?: Repair[]) {
    if (loading || uncertain) return;
    setLoading(true);
    setError('');
    try {
      const response = await importInitialStock(nextFile, mode, crypto.randomUUID());
      if (response.successRows) onSuccess();
      if (retryRows && result) {
        setResult({ ...result, imported: result.imported + response.successRows, jobId: response.importJobId, repairs: [...result.repairs.filter((row) => row.skipped), ...repairsFrom(response, retryRows)] });
      } else {
        setResult({ filename: nextFile.name, total: response.totalRows, imported: response.successRows, jobId: response.importJobId, repairs: repairsFrom(response) });
      }
    } catch (cause) {
      if (!(cause instanceof ApiError) || cause.status >= 500) setUncertain(true);
      setError(cause instanceof Error ? cause.message : '导入失败');
    } finally {
      setLoading(false);
    }
  }

  function changeRepair(index: number, patch: Partial<Repair>) {
    setResult((current) => current && ({ ...current, repairs: current.repairs.map((row, position) => position === index ? { ...row, ...patch } : row) }));
  }

  function toggleSkipped(index: number) {
    const row = result?.repairs[index];
    if (!row || !result) return;
    const skipping = !row.skipped;
    changeRepair(index, { skipped: skipping });
    setExpandedRepair(skipping ? result.repairs.findIndex((item, position) => position !== index && !item.skipped) : index);
  }

  const pending = result?.repairs.filter((row) => !row.skipped) || [];
  const skipped = result?.repairs.filter((row) => row.skipped).length || 0;

  return <div className={s.backdrop} onMouseDown={(event) => { if (event.target === event.currentTarget && !loading) onClose(); }}>
    <section className={s.dialog} role="dialog" aria-modal="true" aria-label={result ? '导入结果' : '导入期初库存'}>
      <header className={s.head}>
        {result ? <button className={s.mobileBack} type="button" onClick={onClose} disabled={loading} aria-label="返回"><Icon name="back" size={22} /></button> : null}
        <h2>{result ? '导入结果' : '导入期初库存'}{result && <small>{result.filename}</small>}</h2>
        {result ? <><button className={s.mobileDone} type="button" onClick={onClose}>完成</button><button className={s.desktopClose} type="button" onClick={onClose} disabled={loading} aria-label="关闭"><Icon name="x" size={18} /></button></> : <button className={s.closeButton} type="button" onClick={onClose} disabled={loading} aria-label="关闭"><Icon name="x" size={18} /></button>}
      </header>
      {result ? <>
        <div className={s.body}>
          <div className={s.kpis}><div><span>总行数</span><b>{result.total}</b></div><div><span>已导入</span><b className={s.good}>{result.imported}</b></div><div><span>待改</span><b className={s.bad}>{pending.length}</b></div></div>
          {pending.length ? <><p className={`${s.hint} ${s.mobileResultHint}`}>{pending.length} 行没通过校验，改完只重导这些行</p><p className={`${s.hint} ${s.desktopResultHint}`}>{pending.length} 行没通过校验，改完只重导这些行。已导入的行不会重复处理。</p></> : <p className={s.hint}>导入完成。{skipped > 0 ? `${skipped} 行已跳过，可下载失败清单另行处理。` : '所有行都已处理。'}</p>}
          <div className={s.mobileRepairList}>
            {result.repairs.map((row, index) => <div className={`${s.mobileRepair} ${row.skipped ? s.skipped : ''}`} key={`mobile-${row.originalRow}-${index}`}>
              {expandedRepair === index && !row.skipped ? <>
                <div className={s.mobileRepairTitle}><b><span>第 {row.originalRow} 行</span>{repairTitle(row)}</b><small>{repairSummary(row, productHints[index]?.name)}</small></div>
                {row.message.startsWith('产品编号不存在') ? <label className={s.mobileRepairField}>产品编号<input value={row.productCode} onChange={(event) => changeRepair(index, { productCode: event.target.value })} /></label>
                  : row.message.startsWith('仓库不存在') ? <label className={s.mobileRepairField}>{row.warehouseCode ? '仓库编码' : '仓库名称'}<input value={row.warehouseCode || row.warehouseName} onChange={(event) => changeRepair(index, row.warehouseCode ? { warehouseCode: event.target.value } : { warehouseName: event.target.value })} /></label>
                    : row.message.includes('期初库存不是数字') ? <label className={s.mobileRepairField}>期初库存<input value={row.quantity} onChange={(event) => changeRepair(index, { quantity: event.target.value })} /></label>
                      : <div className={s.repairGrid}>{(['productCode', 'warehouseCode', 'warehouseName', 'quantity', 'unitCost', 'note'] as const).map((field) => <label key={field}>{({ productCode: '产品编号', warehouseCode: '仓库编码', warehouseName: '仓库名称', quantity: '期初库存', unitCost: '成本单价', note: '备注' })[field]}<input value={row[field]} onChange={(event) => changeRepair(index, { [field]: event.target.value })} /></label>)}</div>}
                <div className={s.mobileFixes}>{productHints[index]?.suggestion && row.productCode !== productHints[index].suggestion.code && <button className={s.suggestion} type="button" onClick={() => changeRepair(index, { productCode: productHints[index].suggestion!.code })}>改为 {productHints[index].suggestion.code} {productHints[index].suggestion.name}</button>}<button type="button" onClick={() => toggleSkipped(index)}>跳过这一行</button></div>
              </> : <button className={s.mobileRepairSummary} type="button" onClick={() => row.skipped ? toggleSkipped(index) : setExpandedRepair(index)}>
                <span className={s.summaryMain}><b><span>第 {row.originalRow} 行</span>{row.skipped ? '已跳过' : repairTitle(row)}</b><small>{repairSummary(row, productHints[index]?.name)}</small></span><span className={s.summaryChevron}>{row.skipped ? '恢复' : '›'}</span>
              </button>}
            </div>)}
          </div>
          <p className={s.importedNote}>{result.imported ? `${result.imported} 行已写入库存，并各生成一条「期初导入」流水。` : '当前没有行写入库存；修复失败行后只重新导入待改行。'}</p>
          <div className={s.desktopRepairs}>{result.repairs.map((row, index) => <div className={`${s.repair} ${row.skipped ? s.skipped : ''}`} key={`desktop-${row.originalRow}-${index}`}>
            <h3>第 {row.originalRow} 行 <span>{row.message}</span></h3>
            {!row.skipped && <div className={s.repairGrid}>
              <label>产品编号<input value={row.productCode} onChange={(event) => changeRepair(index, { productCode: event.target.value })} /></label>
              <label>仓库编码<input value={row.warehouseCode} onChange={(event) => changeRepair(index, { warehouseCode: event.target.value })} /></label>
              <label>仓库名称<input value={row.warehouseName} onChange={(event) => changeRepair(index, { warehouseName: event.target.value })} /></label>
              <label>期初库存<input value={row.quantity} onChange={(event) => changeRepair(index, { quantity: event.target.value })} /></label>
              <label>成本单价<input value={row.unitCost} onChange={(event) => changeRepair(index, { unitCost: event.target.value })} /></label>
              <label>备注<input value={row.note} onChange={(event) => changeRepair(index, { note: event.target.value })} /></label>
            </div>}
            <button type="button" onClick={() => toggleSkipped(index)}>{row.skipped ? '恢复这一行' : '跳过这一行'}</button>
          </div>)}</div>
          {error && <p className={s.error} role="alert">{error}{uncertain && <>。请求结果未确认，请先在 <Link to="/imports" target="_blank">导入历史</Link> 核对，暂勿重复提交。</>}</p>}
        </div>
        <footer className={`${s.foot} ${s.resultFoot}`}><button type="button" className={s.failedCsv} onClick={() => downloadFailed(result.repairs)} disabled={!result.repairs.length}><Icon name="down" size={16} />失败清单</button><button type="button" className={s.footerDone} onClick={onClose}>完成</button><button type="button" className={s.primary} disabled={!pending.length || loading || uncertain} onClick={() => void upload(new File([csvFor(pending)], 'initial-stock-retry.csv', { type: 'text/csv' }), pending)}>{loading ? '正在导入…' : `重新导入 ${pending.length} 行`}</button></footer>
      </> : <>
        <div className={s.body}>
          <p className={s.hint}><span className={s.mobileHint}><b>手机上适合看结果、改错行</b><br />几百行以上的大批量导入，建议在电脑上做：核对 Excel、下载失败清单都更方便。</span><span className={s.desktopHint}>支持 .xlsx / .csv。必填列：<b>产品编号</b>、<b>期初库存</b>，以及<b>仓库编码或仓库名称</b>。</span></p>
          <a className={s.template} href="/api/v2/import/initial-stock"><Icon name="down" size={16} />下载导入模板</a>
          <div className={s.mobileFiles}>
            <a className={s.templateCard} href="/api/v2/import/initial-stock"><span className={s.downloadIcon}><Icon name="down" size={16} /></span><span><b>下载导入模板</b><small>期初库存模板.xlsx · 存到「文件」App</small></span><span className={s.chevron} aria-hidden="true">›</span></a>
            <label className={s.fileCard}><input type="file" accept=".xlsx,.csv" onChange={(event) => selectFile(event.target.files?.[0] || null)} /><span className={s.fileIcon}><Icon name="folder" size={18} /></span><span><b>{file?.name || '从「文件」App 选择'}</b><small>{file ? `${(file.size / 1024).toFixed(0)} KB · 点此更换文件` : '期初库存表 · .xlsx / .csv'}</small></span><span className={s.chevron} aria-hidden="true">›</span></label>
          </div>
          <small className={s.mobileFileHelp}>支持 .xlsx / .csv，单个不超过 12 MB。必填列：产品编号、期初库存，以及仓库编码或仓库名称。</small>
          <fieldset className={s.mode}><legend>导入模式 <em>*</em></legend><label className={mode === 'set' ? s.modeSelected : ''}><input type="radio" checked={mode === 'set'} onChange={() => setMode('set')} /><span>设置为表中数量</span><small>写「调整」流水</small></label><label className={mode === 'add' ? s.modeSelected : ''}><input type="radio" checked={mode === 'add'} onChange={() => setMode('add')} /><span>追加为入库数量</span><small>写「入库」流水</small></label><small className={s.modeHelp}>「设置」会将现有库存改成表中数值；「追加」在现有库存基础上增加。</small></fieldset>
          <label className={`${s.drop} ${dragging ? s.drag : ''}`} onDragOver={(event) => { event.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={(event) => { event.preventDefault(); setDragging(false); selectFile(event.dataTransfer.files[0] || null); }}><input type="file" accept=".xlsx,.csv" onChange={(event) => selectFile(event.target.files?.[0] || null)} /><Icon name="folder" size={24} /><b>{file?.name || <><span className={s.dropTabletText}>拖入文件，或从「文件」App 选择</span><span className={s.dropOtherText}>点击或拖拽文件到这里</span></>}</b><small>单个文件不超过 12 MB</small></label>
          <div className={s.notice}><Icon name="alert" size={18} /><p><b>导入不会静默改数</b><br /><span className={s.desktopHint}>成功行会生成库存流水，可按「期初导入」筛选复核；失败行保留原因，可下载清单修改后重导。</span><span className={s.mobileHint}>每一行都会生成库存流水，可在「出入库流水」按「期初导入」筛选复核。校验失败的行会保留，可逐行改。</span></p></div>
          {error && <p className={s.error} role="alert">{error}{uncertain && <>。请求结果未确认，请先在 <Link to="/imports" target="_blank">导入历史</Link> 核对，暂勿重复提交。</>}</p>}
        </div>
        <footer className={s.foot}><button type="button" className={s.footerCancel} onClick={onClose} disabled={loading}>取消</button><button type="button" className={s.primary} disabled={!file || loading || uncertain} onClick={() => file && void upload(file)}>{loading ? '正在导入…' : '开始导入'}</button></footer>
      </>}
    </section>
  </div>;
}
